import { numberFormatter } from 'components/AgGrid/utils';
import type { BimWorkOrderTotalItemData } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { formatNumberToRu } from 'utils';

import type { ColDef, ColGroupDef, RowClassRules, ValueFormatterParams } from 'ag-grid-community';
import { useMemo } from 'react';

const quantityFormatter = (p: ValueFormatterParams) => {
    const { value, data } = p;

    if (!value) {
        return '';
    }

    if (data?.name?.includes('в т.ч')) {
        return `${formatNumberToRu(value, 2)}%`;
    }
    if (data?.name?.includes('инфляции на')) {
        return `${formatNumberToRu(value, 4)}`;
    }

    return formatNumberToRu(value, 0);
};

export const useBimWorkOrderTotalCommonColumns = <T = any,>({
    workOrderStatus,
    hasInProgress,
    isFrameContract,
}: {
    workOrderStatus: WorkOrderStatusEnum;
    hasInProgress: boolean;
    isFrameContract: boolean;
}) => {
    const planColumns = useMemo<(ColDef | ColGroupDef)[]>(
        () => [
            {
                headerName: 'Стоимость строительно-монтажных работ',
                width: 600,
                children: [
                    {
                        headerName: 'Всего СМР',
                        field: 'smrTotal',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                    {
                        headerName: 'ОЗП',
                        field: 'ozp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                    {
                        headerName: 'ЭММ',
                        field: 'emm',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                    {
                        headerName: 'В том числе',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        children: [
                            {
                                headerName: 'ЗПМ',
                                field: 'zpm',
                                minWidth: 180,
                                spanHeaderHeight: true,
                                valueFormatter: numberFormatter(0),
                            },
                        ],
                        marryChildren: true,
                    },
                    {
                        headerName: 'МП',
                        field: 'mp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                    {
                        headerName: 'НР',
                        field: 'nr',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                    {
                        headerName: 'СП',
                        field: 'sp',
                        minWidth: 120,
                        spanHeaderHeight: true,
                        valueFormatter: numberFormatter(0),
                    },
                ],
                marryChildren: true,
            },
            {
                headerName: 'ОП',
                field: 'op',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(0),
            },
            {
                headerName: 'Прочие затраты',
                field: 'others',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(0),
            },
            {
                headerName: 'Всего',
                field: 'total',
                minWidth: 120,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
            },
            {
                headerName: 'Справочно МЗ',
                field: 'mz',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(0),
            },
            {
                headerName: 'Справочно ОЗ',
                field: 'oz',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(0),
            },
            {
                headerName: 'ТЗР, чел.час',
                field: 'tzr',
                minWidth: 140,
                spanHeaderHeight: true,
                valueFormatter: numberFormatter(2),
            },
        ],
        [],
    );

    const isUseInProgressColumnsSetup = useMemo(
        () =>
            workOrderStatus === WorkOrderStatusEnum.InProgress ||
            workOrderStatus === WorkOrderStatusEnum.Completed ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress),
        [hasInProgress, workOrderStatus],
    );

    const columns = useMemo<ColDef<T>[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name' as any,
                minWidth: 230,
                wrapText: true,
                autoHeight: true,
                cellClass: 'wrappedCenteredCell',
                suppressMovable: true,
            },
            {
                headerName: 'Кол-во',
                field: 'quantity' as any,
                minWidth: 100,
                valueFormatter: quantityFormatter,
            },
            ...(!isUseInProgressColumnsSetup
                ? planColumns
                : [
                      {
                          headerName: `План по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                          children: planColumns,
                          marryChildren: true,
                      },
                  ]),
            {
                headerName: `Выполнено по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                field: 'completedValue.total' as any,
                valueFormatter: numberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                hide: !isUseInProgressColumnsSetup,
                minWidth: 160,
            },
            {
                headerName: `Остаток по ${isFrameContract ? 'НЗ' : 'Расчету'}`,
                field: 'remainderValue.total' as any,
                valueFormatter: numberFormatter(0),
                cellClass: 'ag-right-aligned-cell boldWeight',
                hide: !isUseInProgressColumnsSetup,
                minWidth: 200,
                flex: 1,
            },
        ],
        [isFrameContract, isUseInProgressColumnsSetup, planColumns],
    );

    const bimTotalsTableRowClassRules: RowClassRules = useMemo(
        () => ({
            totalsRow: ({ data }: { data: BimWorkOrderTotalItemData }) =>
                data?.name?.toLowerCase().includes('итого по главам') ||
                data?.name?.toLowerCase().includes('итого по наряд-заказу') ||
                data?.name?.toLowerCase().includes('итого по расчету') ||
                data?.name?.toLowerCase().includes('итого по виду строительства') ||
                data?.name?.toLowerCase().includes('итого с непредвиденными и мотивационными затратами'),
        }),
        [],
    );

    return {
        columns,
        bimTotalsTableRowClassRules,
    };
};
